"use client";

import React, { useEffect } from "react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useProviders } from "@/hooks/use-providers";

import { formatStaffName } from "@/lib/format-data";
import { PERMISSIONS } from "@/lib/permissions";

export interface ProviderSignatureInputProps {
  value: Record<string, any>;
  onChange: (value: Record<string, any>) => void;
  disabled?: boolean;
  defaultProviderId?: string;
}

// Provider-signature input: pick a provider and stamp their adopted signature.
// The answer stores the selected provider + a gs:// snapshot of their signature.
// Warns when the chosen provider has not adopted a signature yet.
export const ProviderSignatureInput: React.FC<ProviderSignatureInputProps> = ({
  value,
  onChange,
  disabled = false,
  defaultProviderId,
}) => {
  const { providers } = useProviders({
    hasFirebaseUid: true,
    permission: PERMISSIONS.SIGN_OFF_SIGNATURE,
  });

  const selectedId: string | undefined =
    typeof value?.providerId === "string" ? value.providerId : undefined;
  const selected = providers.find((p) => p.id === selectedId);

  // Default to the current provider once the list has loaded and nothing is set.
  useEffect(() => {
    if (
      disabled ||
      selectedId ||
      !defaultProviderId ||
      providers.length === 0
    ) {
      return;
    }
    const self = providers.find((p) => p.id === defaultProviderId);
    if (self) {
      onChange({
        providerId: self.id,
        providerName: formatStaffName(self),
        signatureImage: self.signatureImage ?? null,
        signatureImageUrl: self.signatureImageUrl ?? null,
        signedDate: new Date().toISOString(),
      });
    }
    // Only run when the list/default resolves; onChange identity is stable enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, selectedId, defaultProviderId, providers.length]);

  // Prefer the answer's stamped snapshot so the editor matches what the renderer
  // and export show. The live provider signature is only used to fill a new
  // selection (via onChange below); it never overrides an already-stamped answer,
  // otherwise a provider adopting a new signature would retroactively change the
  // image shown for a note stamped earlier.
  const signatureUrl: string | null =
    typeof value?.signatureImageUrl === "string"
      ? value.signatureImageUrl
      : null;
  const hasSignature = !!(value?.signatureImage || signatureUrl);

  if (disabled) {
    return signatureUrl ? (
      <div className="space-y-1">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={signatureUrl}
          alt="Signature"
          className="max-h-24 rounded border border-border bg-background p-1"
        />
        {value?.providerName && (
          <p className="text-caption text-foreground-secondary">
            {value.providerName}
          </p>
        )}
      </div>
    ) : (
      <div className="text-body text-foreground-muted">
        {value?.providerName
          ? `${value.providerName} (no signature on file)`
          : "Not signed"}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Select
        value={selectedId ?? ""}
        onValueChange={(id) => {
          const provider = providers.find((p) => p.id === id);
          if (!provider) return;
          onChange({
            providerId: provider.id,
            providerName: formatStaffName(provider),
            signatureImage: provider.signatureImage ?? null,
            signatureImageUrl: provider.signatureImageUrl ?? null,
            signedDate: new Date().toISOString(),
          });
        }}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select a provider" />
        </SelectTrigger>
        <SelectContent>
          {providers.map((p) => (
            <SelectItem key={p.id} value={p.id}>
              {formatStaffName(p)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {selectedId &&
        (hasSignature && signatureUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={signatureUrl}
            alt="Signature"
            className="max-h-24 rounded border border-border bg-background p-1"
          />
        ) : (
          <p className="text-caption text-status-warning-text">
            {selected
              ? `${formatStaffName(selected)} hasn't adopted a signature yet. Ask them to add one in Settings → Profile.`
              : "This provider hasn't adopted a signature yet."}
          </p>
        ))}
    </div>
  );
};
